import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import dayjs from "dayjs";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetMemberService } from "../../../data/member/get-member.service";
import { UpdateMemberService } from "../../../data/member/update-member.service";
import { GetUserAndBelongsService } from "../../../data/user/get-user-and-belongs.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { UpdateUserService } from "../../../data/user/update-user.service";
import { InviteMembersService } from "../../../data/member/invite-members.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { STAFF_DETAILS_I18N } from "./staff-details.i18n";

@Component({
  selector: "app-staff-details",
  templateUrl: "./staff-details.component.html",
  styleUrls: ["./staff-details.component.scss"],
  imports: SHARED_IMPORTS,
})
export class StaffDetailsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = STAFF_DETAILS_I18N;

  public validId: boolean = false;
  public primaryAccount: boolean = false;
  private ownAccount: boolean = false;
  public staffData: StaffData;

  public mode = StaffDetailMode.disp;
  Mode = StaffDetailMode;

  userFormGroup: FormGroup;
  sexOptions = [
    { display: this.i18n.sex.male, value: "male" },
    { display: this.i18n.sex.female, value: "female" },
    { display: this.i18n.sex.other, value: "other" },
  ];

  private readonly defaultStaffData: StaffData = {
    userId: "",
    lastName: "",
    firstName: "",

    userType: "",
    role: [],
    privileges: [],

    contractStatus: "",
    contractStartAt: "--/--/--",
    contractEndAt: "--/--/--",

    lastModifiedAt: "--/--/--",
    lastModifiedBy: "--",

    jobTitleCode: "",
  };

  updateAccountData: StaffData;
  putParam;
  userTypeDisp;

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];

  selectedStatus = "";
  selectedGroup = "";
  selectedRole = [];
  selectedPrivileges = [];

  accountManageRole = false;
  userManageRole = false;
  measureRole = false;
  referRole = false;

  memberId: string;
  sectionId: string;
  loginUserId: string;
  userId: string;

  // Loading state for skeleton - true initially to show skeleton until data is ready
  isLoading = signal(true);
  isSending = signal(false);
  private savedSuccessfully = false;

  constructor(
    public dialogRef: MatDialogRef<StaffDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    private getUserAndBelongsService: GetUserAndBelongsService,
    private getMemberService: GetMemberService,
    private updateMemberService: UpdateMemberService,
    private router: Router,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private updateUserService: UpdateUserService,
    private inviteMemberService: InviteMembersService,
  ) {
    // this.resetFields();
    this.staffData = this.defaultStaffData;
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.memberId = data?.id;

      let user = this.msalService.instance.getActiveAccount();
      this.loginUserId = user.idTokenClaims["sub"];

      if (this.memberId === this.loginUserId) {
        this.ownAccount = true;
      }

      this.sectionId = this.common.getSelectedSection().sectionId;
    });
  }

  ngOnInit() {
    this.userFormGroup = new FormGroup({
      email: new FormControl("", [Validators.required, Validators.email]),

      lastName: new FormControl("", [Validators.required]),
      firstName: new FormControl("", [Validators.required]),
      userId: new FormControl(""),

      signInName: new FormControl("", [Validators.required]),
      password: new FormControl("", [Validators.required]),
      password_confirm: new FormControl("", [Validators.required]),

      userType: new FormControl(""),

      contractStartAt: new FormControl(""),
      contractEndAt: new FormControl(""),

      contractStatus: new FormControl(""),

      jobTitleCode: new FormControl(""),
    });

    this.common.staffRoles.forEach((role) =>
      this.userFormGroup.addControl(role.code, new FormControl(false)),
    ); // 各roleに対してコントロールを追加

    this.userFormGroup.disable();

    // this.resetFields();
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.userId = this.data?.userId;
      this.mode = this.data?.mode ?? this.Mode.disp;
      this.sectionId = this.data?.sectionId;
      this.userTypeDisp = this.data?.userTypeDisp ?? this.i18n.defaultUserType;

      if (this.mode == this.Mode.invite || this.mode == this.Mode.create) {
        this.enableEdit();
        // No data to fetch for invite/create, turn off loading
        this.isLoading.set(false);
      } else if (this.userId != null) {
        this.getStaff();
      } else {
        // No userId provided and not invite/create mode
        this.isLoading.set(false);
      }
    });
  }

  getStaff() {
    this.isLoading.set(true);

    this.getUserAndBelongsService.getUserAndBelongs(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          const resAny = res as any;
          const userId = resAny.user.userId;

          if (userId === "ANONYMOUS") {
            this.snackBar.open("ERROR: invalid ID", null, {
              duration: 5000,
            });
          } else {
            this.staffData = {
              userId: resAny.user.userId,
              lastName: resAny.user.lastName ?? null,
              firstName: resAny.user.firstName ?? null,

              role: resAny.belongs.permissions.role,
              privileges: resAny.belongs.permissions.privileges,
              userType: resAny.belongs.permissions.userType,

              lastModifiedAt: resAny.belongs.commonInfo.lastModifiedAt,
              lastModifiedBy: resAny.belongs.commonInfo.lastModifiedBy,

              contractStartAt: dayjs(
                resAny.belongs.contractInfo.contractStartAt,
              ).isValid()
                ? dayjs(resAny.belongs.contractInfo.contractStartAt).format(
                    "YYYY/MM/DD",
                  )
                : null,
              contractEndAt: dayjs(
                resAny.belongs.contractInfo.contractEndAt,
              ).isValid()
                ? dayjs(resAny.belongs.contractInfo.contractEndAt).format(
                    "YYYY/MM/DD",
                  )
                : null,
              contractStatus:
                resAny.belongs.contractInfo.contractStatus.toUpperCase(),

              jobTitleCode: resAny.belongs.attributes?.jobTitleCode ?? "OTHER",
            };

            // 読み込んだ値をフォームに反映
            this.userFormGroup.patchValue(this.staffData);
            resAny.belongs.permissions.role.forEach((r) => {
              this.userFormGroup.get(r).setValue(true);
            });

            this.selectedStatus =
              resAny.belongs.contractInfo.contractStatus.toUpperCase();
            this.validId = true;
          }
        },
        error: () => {
          // finalize handles isLoading.set(false)
        },
      });
  }

  onNoClick(): void {
    this.dialogRef.close({ needRefresh: this.savedSuccessfully });
  }

  // resetFields(): void {
  //   this.validId = false;
  //   this.selectedStatus = '';
  //   this.accountManageRole = false;
  //   this.userManageRole = false;
  //   this.measureRole = false;
  //   this.referRole = false;
  //   this.staffData = this.defaultStaffData;
  // }

  onEditOrUploadClicked() {
    // 現在がディスプレイモードなら編集モードに切り替える
    if (this.mode == StaffDetailMode.disp) {
      this.switchToEditMode();
      return;
    }

    //そうでない場合はすでに編集モードなのでデータベースにアップロードする

    var postParams: Record<string, any> = {};

    postParams.firstName =
      this.userFormGroup.value.firstName != ""
        ? this.userFormGroup.value.firstName
        : null;
    postParams.lastName =
      this.userFormGroup.value.lastName != ""
        ? this.userFormGroup.value.lastName
        : null;
    //postParams.birthDate = this.userFormGroup.value.birthDate;
    //postParams.sex = this.userFormGroup.value.sex;
    postParams.email = this.userFormGroup.value.email;
    postParams.jobTitleCode = this.userFormGroup.value.jobTitleCode;

    var roles = [];
    this.common.staffRoles.forEach((r) => {
      if (this.userFormGroup.value[r.code]) {
        roles.push(r.code);
      }
    });
    if (roles.length > 0) {
      postParams.roleUpdateMode = "OVERWRITE";
      postParams.role = roles;
    }

    this.isSending.set(true);
    if (this.mode == this.Mode.edit) {
      this.updateStaff(postParams);
    }
    if (this.mode == this.Mode.invite) {
      this.inviteStaff(postParams);
    }
  }

  switchToEditMode() {
    this.mode = StaffDetailMode.edit;
    this.enableEdit();

    return;
  }

  enableEdit() {
    this.userFormGroup.get("email").enable();

    this.userFormGroup.get("contractStatus").enable();

    this.userFormGroup.get("firstName").enable();
    this.userFormGroup.get("lastName").enable();
    this.userFormGroup.get("jobTitleCode").enable();

    this.common.staffRoles.forEach((r) => {
      this.userFormGroup.get(r.code).enable();
    });
    // this.userFormGroup.get("sex").enable();

    return;
  }

  updateStaff(postParams) {
    this.updateUserService
      .updateUser(this.staffData.userId, postParams)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe(
        (res) => {
          this.savedSuccessfully = true;
          this.snackBar.open(this.i18n.message.updateSuccess(this.userTypeDisp), this.i18n.button.close, { duration: 3000 });
          this.mode = StaffDetailMode.disp;
          this.userFormGroup.disable();
          this.getStaff();
        },
        (error) => {
          this.snackBar.open(this.i18n.message.updateError(this.userTypeDisp), this.i18n.button.close, { duration: 5000 });
        },
      );
  }

  inviteStaff(postParams) {
    this.inviteMemberService
      .inviteMembers(this.sectionId, postParams)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe(
        (res) => {
          let snack = this.snackBar.open(
            this.i18n.message.inviteSuccess(this.userTypeDisp),
            this.i18n.button.close,
            { duration: 30000 },
          );
          snack.afterDismissed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.dialogRef.close();
          });
        },
        (error) => {
          let snack = this.snackBar.open(
            this.i18n.message.inviteError(this.userTypeDisp),
            this.i18n.button.close,
            { duration: 30000 },
          );
          snack.afterDismissed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.dialogRef.close();
          });
        },
      );
  }

  createStaff(postParams) {
    // バックエンドが対応していないのでフロントも未実装
  }
}

export interface StaffData {
  userId: string;
  lastName: string;
  firstName: string;

  userType: string;
  role: string[];
  privileges: KeyValue[];

  contractStatus: string;
  contractStartAt: string;
  contractEndAt: string;

  lastModifiedAt: string;
  lastModifiedBy: string;

  jobTitleCode: string;
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface InUpdateMember {
  userType: string;
  role: string[];
  privileges: KeyValue[];
  contractStatus: string;
}

export namespace AccountManagementPrivileges {
  export namespace CREATE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "CREATE";
  }
  export namespace READ_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "READ";
  }
  export namespace UPDATE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "UPDATE";
  }
  export namespace DELETE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "DELETE";
  }
}

export enum StaffDetailMode {
  disp,
  edit,
  create,
  invite,
}
